/**
 * Download Utility Functions
 *
 * Provides reusable download functionality for profiler exports:
 * - Financials (Excel)
 * - Onepager (PowerPoint)
 * - Full Profile (PowerPoint)
 * - Comparable Companies (Excel)
 */

/**
 * Extracts filename from Content-Disposition header
 * @param {string|null} contentDisposition - Content-Disposition header value
 * @param {string} defaultFilename - Fallback filename if extraction fails
 * @returns {string} Extracted or default filename
 */
export const extractFilename = (contentDisposition, defaultFilename) => {
  if (!contentDisposition) {
    return defaultFilename;
  }

  const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
  if (filenameMatch?.[1]) {
    return decodeURIComponent(filenameMatch[1]);
  }

  return defaultFilename;
};

/**
 * Triggers file download via dynamically created link
 * @param {Blob} blob - File blob to download
 * @param {string} filename - Download filename
 * @param {string} linkId - ID for the download link element
 */
export const triggerDownload = (blob, filename, linkId = 'download-btn') => {
  // Legacy IE support
  if (window.navigator?.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(blob, filename);
    return;
  }

  // Modern browsers
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.id = linkId;
  link.href = blobUrl;
  link.target = '_blank';
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(blobUrl);
};

/**
 * Downloads a file from the profiler API with authorization
 *
 * @param {Object} options - Download options
 * @param {string} options.url - API endpoint URL
 * @param {string} options.token - Authorization token
 * @param {string} options.defaultFilename - Fallback filename
 * @param {string} options.defaultContentType - Fallback content type
 * @param {string} [options.linkId] - ID for download link element
 * @returns {Promise<{success: boolean, error?: string}>} Download result
 *
 * @example
 * const result = await downloadProfilerFile({
 *   url: `${API_URL}/export-profiler-financials?companyId=${companyId}`,
 *   token: authToken,
 *   defaultFilename: 'transact-digital_financials.xlsx',
 *   defaultContentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
 *   linkId: 'download-financials-btn'
 * });
 *
 * if (!result.success) {
 *   console.error(result.error);
 * }
 */
export const downloadProfilerFile = async ({
  url,
  token,
  defaultFilename,
  defaultContentType,
  linkId = 'download-btn'
}) => {
  // Validate token
  if (!token) {
    return { success: false, error: 'No authorization token' };
  }

  try {
    const response = await fetch(url, {
      headers: {
        Authorization: token,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const contentType = response.headers?.get?.('Content-Type') || '';
      let body = null;
      if (contentType.includes('application/json')) {
        try {
          body = await response.json();
        } catch {
          /* swallow — non-JSON body, fall through with body=null */
        }
      }
      return { success: false, error: 'Response not ok', body, status: response.status };
    }

    // Extract metadata from headers
    const contentDisposition = response.headers.get('Content-Disposition');
    const contentType = response.headers.get('Content-Type') || defaultContentType;
    const filename = extractFilename(contentDisposition, defaultFilename);

    // Create blob and trigger download
    const arrayBuffer = await response.arrayBuffer();
    const blob = new Blob([arrayBuffer], { type: contentType });
    triggerDownload(blob, filename, linkId);

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Download configuration for each export type
 */
export const DOWNLOAD_CONFIGS = {
  financials: {
    endpoint: '/export-profiler-financials',
    defaultFilename: 'transact-digital_financials.xlsx',
    defaultContentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    linkId: 'download-financials-btn',
    errorMessage: 'Failed to download financials.'
  },
  onepager: {
    endpoint: '/export-profiler-slides',
    queryParams: { type: 'onepager' },
    defaultFilename: 'transact-digital_profiler-onepager.pptx',
    defaultContentType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    linkId: 'download-onepager-btn',
    errorMessage: 'Failed to download onepager.'
  },
  'full-profile': {
    endpoint: '/export-profiler-slides',
    queryParams: { type: 'extended' },
    defaultFilename: 'transact-digital_profiler-extended.pptx',
    defaultContentType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    linkId: 'download-fullprofiler-btn',
    errorMessage: 'Failed to download full profile.'
  },
  'comparable-companies': {
    endpoint: '/export-profiler-comparable-companies',
    defaultFilename: 'td_company-name_comparables.xlsx',
    defaultContentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    linkId: 'download-comparable-companies-btn',
    errorMessage: 'Failed to download comparable companies.'
  },
  contacts: {
    endpoint: '/export-profiler-company-contacts',
    defaultFilename: 'transact-digital_contacts.xlsx',
    defaultContentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    linkId: 'download-contacts-btn',
    errorMessage: 'Failed to download contacts.'
  }
};

/**
 * Builds the full URL for a download endpoint
 *
 * @param {string} baseUrl - API base URL
 * @param {string} downloadType - Type of download (financials, onepager, etc.)
 * @param {Object} params - URL parameters
 * @param {string} params.companyId - Company ID
 * @param {string} [params.comparablesId] - Comparables ID (for comparable-companies)
 * @param {boolean} [params.isFromFeedbackProject] - Whether from feedback project
 * @param {Object} [params.project] - Project object with id and ownerEmail
 * @returns {string} Full URL with query parameters
 */
export const buildDownloadUrl = (baseUrl, downloadType, params) => {
  const config = DOWNLOAD_CONFIGS[downloadType];
  if (!config) {
    throw new Error(`Unknown download type: ${downloadType}`);
  }

  const url = new URL(`${baseUrl}${config.endpoint}`);

  // Add common params
  if (downloadType === 'comparable-companies') {
    url.searchParams.set('comparablesId', params.comparablesId);
    if (params.isFromFeedbackProject && params.project) {
      url.searchParams.set('sharedProjectId', params.project.id);
      url.searchParams.set('sharedProjectOwner', params.project.ownerEmail);
    }
  } else {
    url.searchParams.set('companyId', params.companyId);
  }

  // Add type-specific query params
  if (config.queryParams) {
    Object.entries(config.queryParams).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });
  }

  return url.toString();
};
